import { buildQueryParamLink } from 'lib/helpers/urlHelper';
import { ISwrConfiguration, useFetch } from 'lib/hooks';
import { ISearchResult } from 'lib/types';
import { ILocation, UserProfileRole } from 'lib/types/user';
import _ from 'lodash';
import { useMemo, useState } from 'react';
import { ISelectPopulationModalContext } from './context';

interface ISearchQueryParams {
  searchTerm?: string;
  limit: number;
  offset: number;
}

export const useSearchDepartments = (
  queryParams: ISearchQueryParams,
  options?: ISwrConfiguration
) =>
  useFetch<ISearchResult<string>>(
    buildQueryParamLink(
      '/admin/dashboard/departments/search',
      queryParams,
      true
    ),
    options
  );

export const useSearchCustomCategoryValues = (
  queryParams: ISearchQueryParams,
  options?: ISwrConfiguration
) =>
  useFetch<ISearchResult<string>>(
    buildQueryParamLink(
      '/admin/dashboard/custom-category-values/search',
      queryParams,
      true
    ),
    options
  );

interface IUseSelectPopulationModalConfig {
  onClose: () => void;
  /** Default name of the population, used in labels + copy. */
  populationName?: string;
  /**
   * Total number of users that can be selected.
   * If provided, a "Select All" button will be displayed.
   */
  selectAllPopulationCount?: number;
}

/** Create an 'instance' of a `SelectPopulationModal` and return all of its state and methods. */
export const useSelectPopulationModal = ({
  onClose,
  populationName = 'Team Members',
  selectAllPopulationCount = 0,
}: IUseSelectPopulationModalConfig): Omit<
  ISelectPopulationModalContext,
  'handleSubmit'
> => {
  const [isSelectAll, setIsSelectAll] = useState<boolean>(false);
  const [isPreview, setIsPreview] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');

  const [selectedCustomCategoryValue, setSelectedCustomCategoryValue] =
    useState<string>(undefined);
  const [selectedDepartment, setSelectedDepartment] =
    useState<string>(undefined);
  const [selectedEmployeeType, setSelectedEmployeeType] =
    useState<string>(undefined);
  const [selectedLocations, setSelectedLocations] = useState<{
    locations: ILocation[];
    exclude: boolean;
  }>({ locations: [], exclude: false });
  const [selectedRole, setSelectedRole] = useState<UserProfileRole>(undefined);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);

  const [deselectedPreviewUserIds, setDeselectedPreviewUserIds] = useState<
    number[]
  >([]);

  const isSelectAllVisible =
    selectAllPopulationCount > 0 &&
    _.isEmpty(searchTerm) &&
    _.isEmpty(selectedLocations.locations) &&
    _.isUndefined(selectedCustomCategoryValue) &&
    _.isUndefined(selectedDepartment) &&
    _.isUndefined(selectedEmployeeType) &&
    _.isUndefined(selectedRole);

  const selectedCount: number = useMemo(
    () =>
      isSelectAll
        ? selectAllPopulationCount -
          _.size(selectedUserIds) -
          _.size(deselectedPreviewUserIds)
        : _.size(selectedUserIds) - _.size(deselectedPreviewUserIds),
    [
      deselectedPreviewUserIds,
      isSelectAll,
      selectAllPopulationCount,
      selectedUserIds,
    ]
  );

  const getIsSelected = (userId: number): boolean =>
    isSelectAll
      ? !_.includes(selectedUserIds, userId)
      : _.includes(selectedUserIds, userId);

  const handleClose = () => {
    setSearchTerm('');

    setSelectedCustomCategoryValue(undefined);
    setSelectedDepartment(undefined);
    setSelectedEmployeeType(undefined);
    setSelectedLocations({ locations: [], exclude: false });
    setSelectedRole(undefined);
    setSelectedUserIds([]);

    setIsPreview(false);
    setIsSelectAll(false);

    onClose();
  };

  const handleSelectAll = () => {
    setIsSelectAll(true);
    setSelectedUserIds([]);
  };

  const handleDeselectAll = () => {
    setIsSelectAll(false);
    setSelectedUserIds([]);
  };

  const handleTableRowClick = (userId: number) => {
    if (isPreview) {
      if (selectedCount === 1) {
        setIsSelectAll(false);
        setSelectedUserIds([]);
        setDeselectedPreviewUserIds([]);
        setIsPreview(false);
      } else {
        setDeselectedPreviewUserIds([...deselectedPreviewUserIds, userId]);
      }
    } else {
      const newSelectedUserIds = _.includes(selectedUserIds, userId)
        ? _.without(selectedUserIds, userId)
        : [...selectedUserIds, userId];

      setSelectedUserIds(newSelectedUserIds);
    }
  };

  const handleTogglePreview = () => {
    if (isPreview) {
      setSelectedUserIds(
        isSelectAll
          ? _.concat(selectedUserIds, deselectedPreviewUserIds)
          : _.difference(selectedUserIds, deselectedPreviewUserIds)
      );
      setDeselectedPreviewUserIds([]);
    }
    setIsPreview(!isPreview);
  };

  return {
    deselectedPreviewUserIds,
    getIsSelected,
    handleClose,
    handleDeselectAll,
    handleSelectAll,
    handleTableRowClick,
    handleTogglePreview,
    isPreview,
    isSelectAll,
    isSelectAllVisible,
    populationName,
    selectedCount,
    selectedCustomCategoryValue,
    selectedDepartment,
    selectedEmployeeType,
    selectedLocations,
    selectedRole,
    selectedUserIds,
    searchTerm,
    setSearchTerm,
    setSelectedCustomCategoryValue,
    setSelectedDepartment,
    setSelectedEmployeeType,
    setSelectedLocations,
    setSelectedRole,
  };
};
